import * as React from "react";

import classNames from "classnames";
import AiAssistantIcon from "images/ai_assistant_icon.svg";

import {
  find,
  includes,
  intersection,
  isEmpty,
  kebabCase,
  replace,
  upperCase,
} from "lodash";

import { findNode } from "../../../../helpers/Doc/Functions/base";

import IconCircleExpandImage from "images/icon-circle-expand-1.svg";
import IconCircleCollapseImage from "images/icon-circle-collapse-1.svg";

import MultiUseImage from "images/multi-use.jpg";
import SingleUseImage from "images/single-use.jpg";

import { OPENED_SECTION_LOG } from "../../../../helpers/DocumentUserLog";

import { MEDIA_PICKER } from "../../../../helpers/Doc/menu";

import DocStyledText from "../../BlockNodeHelpers/StyledText";

import { TEACHING_POINT_CLASSES } from "../../../../helpers/RO/content_type";

import AqButton from '../../../Aq/Button';

interface DocSectionHeaderProps extends DocSectionType<InlineNode>, BlockNodeAll {
  integrationInfo: IntegrationInfo;
  openSiblingSectionUIDs: Array<string>;
  paused?: boolean;
  roData: RoData;
  roDocumentCount: number;
  submitted?: boolean;
};

function DocSectionHeader(props: DocSectionHeaderProps) {
  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;

    const buttons = Array.from(document.querySelectorAll('.aet-plus-minus-button'));
    const idx = buttons.indexOf(document.activeElement);
    if (idx === -1) return;

    e.preventDefault();

    let nextIdx: number;
    if (e.key === 'ArrowRight') {
      nextIdx = (idx + 1) % buttons.length;
    } else {
      nextIdx = (idx - 1 + buttons.length) % buttons.length;
    }

    setTimeout(() => {
      const nextButton = buttons[nextIdx] as HTMLButtonElement;
      nextButton.focus();
      nextButton.setAttribute('aria-expanded', nextButton.classList.contains('icon-close-box') ? 'true' : 'false');
      buttons.forEach((button, buttonIdx) => {
        if (buttonIdx === nextIdx) {
          button.classList.add('current-element');
        } else {
          button.classList.remove('current-element');
        }
      });
    }, 0);
  }
  const {
    aiDisplayFlag,
    currentVersion,
    editingContext,
    displayContext,
    userContext,
    classes,
    collapsed,
    collapsible,
    inactiveBool,
    level,
    title,
    uid,
    openSiblingSectionUIDs,
    disableStyleTextEditing,
    roData,
    roDocumentCount = 1,
  } = props;

  const onHeaderClick = () => {
    displayContext.setFocusedBookmarkUid(null);

    if (inactiveBool) { return null; }

    if (collapsible) {
      const newState = { [uid]: {collapsed: !collapsed} };

      if (collapsed) {
        userContext.logEvent(OPENED_SECTION_LOG, {node_uid: uid});

        if (currentVersion.onlyOneUncollapsedSection && level === 1 && openSiblingSectionUIDs && !displayContext.canBeEditing) {
          openSiblingSectionUIDs.forEach((uid) => {
            newState[uid] = {collapsed: true};
          });
        }

      }

      userContext.addState(newState);

      const escapedUid = CSS.escape(uid);
      const button = document.querySelector(`#${escapedUid}_header .aet-plus-minus-button`) as HTMLButtonElement;
      if (button) {
        const expandedState = collapsed ? 'expanded' : 'collapsed';
        button.setAttribute('aria-expanded', collapsed ? 'true' : 'false');
        button.setAttribute('aria-label', `${title} is ${expandedState}`);
      }
    }
  };

  let collapsedIndicator;
  if (collapsible) {
    if (!isEmpty(intersection(["neutral_text", "core_concept_header"], classes))) {
      if (collapsed) {
        collapsedIndicator = <span className="doc-section-collapsed-indicator"><img src={IconCircleExpandImage} alt="" role="presentation" className="neutral-text-collapse-icon" /></span>;
      } else {
        collapsedIndicator = <span className="doc-section-collapsed-indicator"><img src={IconCircleCollapseImage} alt="" role="presentation" className="neutral-text-collapse-icon" /></span>;
      }
    } else {
      if (collapsed) {
        collapsedIndicator = (
          <span className="doc-section-collapsed-indicator" style={{ display: 'flex' }}>
            <button
              className="icon-open-box aet-plus-minus-button"
              aria-label="Expand section"
              aria-expanded={!collapsed}
              aria-controls={`${uid}_section_body`}
              onClick={onHeaderClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onHeaderClick();
                  (e.target as HTMLButtonElement).focus();
                }
                handleKeyDown(e);
              }}
            ></button>
          </span>
        );
      } else {
        collapsedIndicator = (
          <span className="doc-section-collapsed-indicator" style={{ display: 'flex' }}>
            <button
              className="icon-close-box aet-plus-minus-button"
              aria-label="Collapse section"
              aria-expanded={!collapsed}
              aria-controls={`${uid}_section_body`}
              onClick={onHeaderClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onHeaderClick();
                  (e.target as HTMLButtonElement).focus();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  onHeaderClick();
                }
                handleKeyDown(e);
              }}
            ></button>
          </span>
        );
      }
    }
  }

  let coverImage;
  if (level === 0 && editingContext.isEditing) {
    const startCoverEditAction = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      editingContext.setCurrentMenu(MEDIA_PICKER, { mediaType: "image", uid: uid });
    };

    coverImage = (
      <div className="doc-section-header-cover-image" onClick={startCoverEditAction}>
        <img src={currentVersion.coverImage} />
      </div>
    );
  }

  let addLOButton;
  if (props?.featureFlag?.saq_phase_1 && editingContext.isEditing && classes?.includes("learning_objectives_card")) {
    addLOButton = (
      <span style={{marginRight: '10px'}}>
        <AqButton
          className="pink outline small"
          onClick={() => {
            editingContext.setCurrentMenu("LEARNING_OBJECTIVES_ADD_NEW");
          }}
        >
          <div className="aq-button-text">
            ADD LO
          </div>
        </AqButton>
      </span>
    );
  }

  let rightHeader = null;
  const cardClass = find(classes, (cl) => {return cl.includes("_card");});
  if (cardClass) {
    const cardC = replace(cardClass, "_card", "");
    const cardText = upperCase(cardC);

    const cardIcon = <span className={kebabCase(`icon-${cardC}`)}></span>;

    rightHeader = (
      <div className="doc-section-header-right">
        <h3 className="doc-section-header-right-title">{cardText}{cardIcon}</h3>
      </div>
    );
  }

  let topper = null;
  if (!isEmpty(intersection([...TEACHING_POINT_CLASSES,"expert_comment"], classes))) {

    let icon = "icon-star-circle";
    let topperTitle = "TEACHING POINT";
    let faIcon = null;
    let topperCollapsedIndicator = null;
    let sectionTitle = null;

    if (includes(classes, "underlying_core_concepts")) {
      icon = "";
      faIcon = <i key="fa-list-ul" className="fa fa-list-ul" aria-hidden="true"></i>;
      topperTitle = "Underlying Core Concepts";

      topper = (
        <div className="core-concepts-topper">
          <span className={`core-concepts-topper-icon ${icon}`}>{faIcon}</span>
          <span className="core-concepts-topper-title">{topperTitle} </span>
        </div>
      );
    } else if (includes(classes, "expert_comment")) {
      icon = "";
      faIcon = <i key="fa-anchor-icon" className="fa fa-anchor" aria-hidden="true"></i>;
      topperTitle = "Deep Dive";
      sectionTitle = (
        <span className="teaching-point-section-topper-title">
          <DocStyledText
            uid={uid}
            content={title}
            contentKey={"title"}
            editingContext={editingContext}
            placeholder={"Enter Deep Dive title here"}
            independentlyEditable
            disableStyleTextEditing={disableStyleTextEditing}
          />
        </span>
      );

      if (collapsible) {
        if (collapsed) {
          topperCollapsedIndicator = <img src={IconCircleExpandImage} alt="" role="presentation" className="expert-comment-collapse-icon" />;
        } else {
          topperCollapsedIndicator = <img src={IconCircleCollapseImage} alt="" role="presentation" className="expert-comment-collapse-icon" />;
        }
      }
    }

    topper = (
      <div className="teaching-point-topper" onClick={onHeaderClick}>
        <div className="teaching-point-topper-content">
          {topperCollapsedIndicator}
          {roData && <img src={roDocumentCount > 1 ? MultiUseImage : SingleUseImage}  height="25" width="25"/>}
          <span className={`teaching-point-topper-icon ${icon}`}>{faIcon}</span>
          <span className="teaching-point-topper-title">{topperTitle}</span>
          {sectionTitle}
        </div>
        <div className="teaching-point-topper-border" />
      </div>
    );
  }

  const sectionPlaceholder = () => {
    if (level === 1) {
      return "Enter Page title here";
    } else if (level > 1) {
      if (includes(classes, "neutral_text")) {            return "Enter Neutral text here";}
      else if (includes(classes, "core_concept_header")) {return "Enter Core Concept header here"; }
      else if (includes(classes, "dialog_box")) {         return "Enter Dialog Box text here"; }
      else if (includes(classes, "alert")) {              return "Enter Alert text here"; }
      else if (includes(classes, "clinical_reasoning")) { return "Enter Clinical Reasoning text here"; }
      else if (includes(classes, "clinical_skills")) {    return "Enter Clinical Skills text here"; }
      else if (includes(classes, "knowledge")) {          return "Enter Knowledge text here"; }
      else if (includes(classes, "management")) {         return "Enter Management text here"; }
      else if (includes(classes, "principles")) {         return "Enter Principles text here"; }
      else if (includes(classes, "studies")) {            return "Enter Studies text here"; }
      else                                                return "Enter Section text here";
    } else {
      return "Enter Section text here";
    }
  };

  const hasAiChildren = level === 1 && !!findNode(props, (node) => ["aiAssistantChat", "aiAssistantFeedback"].includes(node.type))

  // Use semantic heading tag based on level, always with className 'doc-section-header-title'
  const HeadingTag = `h${Math.min(level + 1, 6)}` as keyof JSX.IntrinsicElements;

  return (
    <div
      id={`${uid}_header`}
      className={classNames("doc-section-header", { "display-root": level === 0 })}
      onClick={onHeaderClick}
    >
      {topper}
      <div className="doc-section-header-banner">
        <div className="doc-section-header-title">
          {coverImage}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {collapsedIndicator}
            <HeadingTag className="doc-section-header-title-heading" style={{ fontWeight: "inherit" }}>
              <DocStyledText
                uid={uid}
                content={title}
                contentKey={"title"}
                placeholder={sectionPlaceholder()}
                editingContext={editingContext}
                independentlyEditable
                disableStyleTextEditing={disableStyleTextEditing}
                noSpan
              />
            </HeadingTag>
            {aiDisplayFlag === "aiOn" && hasAiChildren && (
              <>
                <img
                  src={AiAssistantIcon}
                  alt="AI Assistant"
                  style={{ width: 60, height: 60, marginLeft: 40 }}
                />
              </>
            )}
          </div>
        </div>
        <span style={{marginRight: '10px'}}>
          {addLOButton}
        </span>
        {rightHeader}
      </div>
    </div>
  );
}

export default React.memo(DocSectionHeader);
